import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import "../css/productdetails.scss";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, wishlistItems } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openPanel, setOpenPanel] = useState("details");
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setSelectedSize(product?.sizes?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
  }, [product]);

  useEffect(() => {
    if (!added) return undefined;
    const timer = window.setTimeout(() => setAdded(false), 1700);
    return () => window.clearTimeout(timer);
  }, [added]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const fallback = productData.filter(
      (item) => item.id !== product.id && !sameCategory.some((same) => same.id === item.id)
    );
    return [...sameCategory, ...fallback].slice(0, 4);
  }, [product]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found">
        <p className="eyebrow">Product unavailable</p>
        <h1>We couldn’t find that piece.</h1>
        <p>It may have moved, or the collection may have changed.</p>
        <Link className="button button--dark" to="/shop" onClick={scrollToTop}>
          Return to shop
        </Link>
      </main>
    );
  }

  const isWishlisted = wishlistItems.includes(product.id);

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
  };

  return (
    <main className="product-detail">
      <nav className="product-detail__breadcrumbs page-shell" aria-label="Breadcrumb">
        <Link to="/shop" onClick={scrollToTop}>Shop</Link>
        <span aria-hidden="true">/</span>
        <Link
          to={`/shop?category=${encodeURIComponent(product.category)}`}
          onClick={scrollToTop}
        >
          {product.category}
        </Link>
        <span aria-hidden="true">/</span>
        <span>{product.name}</span>
      </nav>

      <section className="product-detail__layout page-shell">
        <div className="product-detail__gallery">
          <div className="product-detail__thumbs" aria-label="Product images">
            {product.images.map((image, index) => (
              <button
                type="button"
                key={image}
                className={selectedImage === index ? "is-active" : ""}
                onClick={() => setSelectedImage(index)}
                aria-label={`Show image ${index + 1} of ${product.images.length}`}
                aria-pressed={selectedImage === index}
              >
                <img src={image} alt="" width="1120" height="1400" />
              </button>
            ))}
          </div>
          <div className="product-detail__main-image">
            <img
              src={product.images[selectedImage]}
              alt={`${product.name}, product view ${selectedImage + 1}`}
              width="1120"
              height="1400"
            />
            <span className="product-detail__image-count">
              {String(selectedImage + 1).padStart(2, "0")} / {String(product.images.length).padStart(2, "0")}
            </span>
            <div className="product-detail__image-nav">
              <button
                type="button"
                onClick={() =>
                  setSelectedImage((selectedImage - 1 + product.images.length) % product.images.length)
                }
                aria-label="Previous image"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => setSelectedImage((selectedImage + 1) % product.images.length)}
                aria-label="Next image"
              >
                →
              </button>
            </div>
          </div>
        </div>

        <aside className="product-detail__info">
          {product.badge && <p className="eyebrow product-detail__badge">{product.badge}</p>}
          <h1>{product.name}</h1>
          <div className="product-detail__price-row">
            <p>{formatPrice(product.price)}</p>
            <span>Tax included</span>
          </div>
          <div className="product-detail__rating" aria-label={`${product.rating} out of 5 stars`}>
            <span aria-hidden="true">★★★★★</span>
            <span>{product.rating} · {product.reviews} reviews</span>
          </div>
          <p className="product-detail__description">{product.description}</p>

          <fieldset className="product-detail__option">
            <legend>
              Colour <span>{selectedColor}</span>
            </legend>
            <div className="product-detail__chips">
              {product.colors.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={selectedColor === color ? "is-active" : ""}
                  onClick={() => setSelectedColor(color)}
                  aria-pressed={selectedColor === color}
                >
                  {color}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="product-detail__option">
            <legend>
              Size <span>{selectedSize}</span>
            </legend>
            <div className="product-detail__chips">
              {product.sizes.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={selectedSize === size ? "is-active" : ""}
                  onClick={() => setSelectedSize(size)}
                  aria-pressed={selectedSize === size}
                >
                  {size}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="product-detail__purchase">
            <div className="product-detail__quantity" aria-label="Quantity selector">
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span>{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((current) => current + 1)}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
            <button type="button" className="product-detail__add" onClick={handleAddToCart}>
              {added ? "Added to bag" : `Add to bag · ${formatPrice(product.price * quantity)}`}
            </button>
            <button
              type="button"
              className={`product-detail__save${isWishlisted ? " is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-pressed={isWishlisted}
              aria-label={`${isWishlisted ? "Remove from" : "Add to"} wishlist`}
            >
              <span aria-hidden="true">{isWishlisted ? "♥" : "♡"}</span>
            </button>
          </div>

          <button
            type="button"
            className="product-detail__checkout-link"
            onClick={() => {
              handleAddToCart();
              navigate("/checkout");
              scrollToTop();
            }}
          >
            Buy now
          </button>

          <div className="product-detail__accordions">
            {[
              ["details", "Details & care"],
              ["delivery", "Delivery & returns"],
            ].map(([panel, label]) => (
              <div className="product-detail__accordion" key={panel}>
                <button
                  type="button"
                  onClick={() => setOpenPanel(openPanel === panel ? "" : panel)}
                  aria-expanded={openPanel === panel}
                >
                  {label} <span aria-hidden="true">{openPanel === panel ? "−" : "+"}</span>
                </button>
                {openPanel === panel && panel === "details" && (
                  <div>
                    <ul>{product.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
                    <dl>
                      {Object.entries(product.attributes).map(([labelText, value]) => (
                        <React.Fragment key={labelText}>
                          <dt>{labelText}</dt>
                          <dd>{value}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
                {openPanel === panel && panel === "delivery" && (
                  <div>
                    <p>
                      Tracked delivery is {formatPrice(siteConfig.shipping.standardRate)}
                      per order.
                    </p>
                    <p>Unused items may be returned within 30 days. See our full policy before ordering.</p>
                    <Link to="/shipping" onClick={scrollToTop}>Shipping information ↗</Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="product-detail__related page-shell" aria-labelledby="related-products">
        <div className="section-heading section-heading--split">
          <div>
            <p className="eyebrow">You might also like</p>
            <h2 id="related-products">Keep looking</h2>
          </div>
          <Link className="text-link" to="/shop" onClick={scrollToTop}>Shop all ↗</Link>
        </div>
        <div className="product-detail__related-grid">
          {relatedProducts.map((item) => (
            <article key={item.id}>
              <Link to={`/shopDetails/${item.id}`} onClick={scrollToTop}>
                <img
                  src={item.image}
                  alt={item.name}
                  width="1120"
                  height="1400"
                  loading="lazy"
                />
                <div><h3>{item.name}</h3><p>{formatPrice(item.price)}</p></div>
              </Link>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
